import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useShopFile } from '../../components/ShopFileLayout';
import { PageBody } from '../../components/Page';
import { Badge } from '../../components/Pill';
import { StatusBanner } from '../../components/StatusBanner';
import { intlLocale } from '../../i18n/formats';
import { useLocale } from '../../i18n/LocaleProvider';
import type { SupportedLocale } from '../../i18n';
import { isShopAccessRequired, opsErrorMessage } from '../../lib/ops-api';
import {
  diagnoseOrder,
  fetchCannedResponses,
  type Diagnosis,
  type DiagnosisAction,
} from '../../lib/ops-diagnosis-api';
import {
  LOOKUP_BASIS_KEYS,
  SUBJECT_KEYS,
  actionView,
  documentNameKey,
  snippetBody,
  snippetLanguageFor,
  verdictKey,
  verdictTone,
  type DiagnosisVerdict,
} from '../../lib/diagnosis';

/** A date-time as the reader's locale writes it, or a dash. */
function when(
  value: string | null | undefined,
  locale: SupportedLocale,
): string {
  if (!value) return '—';
  return new Date(value).toLocaleString(intlLocale(locale), {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

/** One labelled fact in a definition grid. */
function Fact({
  label,
  children,
}: {
  readonly label: string;
  readonly children: ReactNode;
}) {
  return (
    <div className="min-w-0">
      <dt className="text-xs font-medium text-gray-500">{label}</dt>
      <dd className="text-sm text-gray-900 break-words">{children}</dd>
    </div>
  );
}

/** Yes/no/unknown as a badge. */
function YesNo({ value }: { readonly value: boolean | null }) {
  const { t } = useLocale();
  if (value === null) return <span className="text-gray-500">—</span>;
  return (
    <Badge tone={value ? 'green' : 'gray'}>
      {value ? t('common.yes') : t('common.no')}
    </Badge>
  );
}

/** The verdict sentence, with the document it is about named in it. */
function useVerdictText() {
  const { t } = useLocale();
  return (
    verdict: DiagnosisVerdict,
    documentType: Diagnosis['invoice']['documentType'],
    subject?: Diagnosis['corrections']['items'][number]['subject'],
  ): string => {
    const nameKey = documentNameKey(documentType, subject);
    return t(verdictKey(verdict), {
      document: nameKey ? t(nameKey) : '',
    });
  };
}

/** The canned response for a merchant action, in the shop's language, ready to copy. */
function Snippet({
  snippetKey,
  shopLocale,
}: {
  readonly snippetKey: string;
  readonly shopLocale: string;
}) {
  const { t } = useLocale();
  const [language, setLanguage] = useState(() =>
    snippetLanguageFor(shopLocale),
  );
  const [copied, setCopied] = useState(false);
  const snippets = useQuery({
    queryKey: ['canned-responses', 'active'],
    queryFn: fetchCannedResponses,
    staleTime: 5 * 60 * 1000,
  });

  if (snippets.isLoading) {
    return <p className="text-sm text-gray-500">{t('common.loading')}</p>;
  }
  const snippet = snippets.data?.find((row) => row.key === snippetKey);
  if (!snippet) {
    return (
      <p className="text-sm text-gray-500">{t('diagnosis.snippet.none')}</p>
    );
  }
  const body = snippetBody(snippet, language);

  return (
    <div className="space-y-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-medium text-gray-900">
          {t('diagnosis.snippet.title')}
        </p>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className={
              language === 'de' ? 'btn-primary btn-sm' : 'btn-secondary btn-sm'
            }
            onClick={() => setLanguage('de')}
          >
            {t('diagnosis.snippet.langDe')}
          </button>
          <button
            type="button"
            className={
              language === 'en' ? 'btn-primary btn-sm' : 'btn-secondary btn-sm'
            }
            onClick={() => setLanguage('en')}
          >
            {t('diagnosis.snippet.langEn')}
          </button>
          <button
            type="button"
            className="btn-secondary btn-sm"
            onClick={() => {
              void navigator.clipboard.writeText(body).then(() => {
                setCopied(true);
                window.setTimeout(() => setCopied(false), 2000);
              });
            }}
          >
            {copied
              ? t('diagnosis.snippet.copied')
              : t('diagnosis.snippet.copy')}
          </button>
        </div>
      </div>
      <p className="whitespace-pre-line text-sm text-gray-800">{body}</p>
      <p className="text-xs text-gray-500">{t('diagnosis.snippet.hint')}</p>
    </div>
  );
}

/** The one next action of a family. */
function NextAction({
  action,
  shopLocale,
}: {
  readonly action: DiagnosisAction;
  readonly shopLocale: string;
}) {
  const { t } = useLocale();
  const { shopId, ticketId } = useShopFile();
  const view = actionView(action, { shopId, ticketId });

  switch (view.kind) {
    case 'link':
      return (
        <div className="space-y-1">
          <Link to={view.to} className="btn-primary btn-sm">
            {t(view.labelKey)}
          </Link>
          <p className="text-sm text-gray-600">{t(view.hintKey)}</p>
        </div>
      );
    case 'merchant':
      return (
        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-900">
            {t('diagnosis.action.merchantLead')}
          </p>
          <p className="text-sm text-gray-800">{t(view.instructionKey)}</p>
          {view.snippetKey && (
            <Snippet snippetKey={view.snippetKey} shopLocale={shopLocale} />
          )}
        </div>
      );
    case 'wait':
      return <p className="text-sm text-gray-800">{t(view.messageKey)}</p>;
    case 'escalate':
      return (
        <div className="space-y-1">
          <p className="text-sm font-medium text-red-900">
            {t('diagnosis.action.escalateLead')}
          </p>
          <p className="text-sm text-gray-800">{t(view.messageKey)}</p>
          {view.failedJobId && (
            <p className="text-xs text-gray-500">
              {t('diagnosis.action.failedJob', { id: view.failedJobId })}
            </p>
          )}
        </div>
      );
    case 'none':
      return (
        <p className="text-sm text-gray-600">{t('diagnosis.action.none')}</p>
      );
  }
}

/** One family: the verdict sentence first, then what to do. */
function FamilyCard({
  title,
  verdict,
  documentType,
  subject,
  action,
  shopLocale,
  children,
}: {
  readonly title: string;
  readonly verdict: DiagnosisVerdict;
  readonly documentType: Diagnosis['invoice']['documentType'];
  readonly subject?: Diagnosis['corrections']['items'][number]['subject'];
  readonly action: DiagnosisAction;
  readonly shopLocale: string;
  readonly children?: ReactNode;
}) {
  const { t } = useLocale();
  const verdictText = useVerdictText();
  return (
    <section className="card space-y-3">
      <h2 className="text-base font-semibold text-gray-900">{title}</h2>
      <StatusBanner tone={verdictTone(verdict, action.kind)}>
        {verdictText(verdict, documentType, subject)}
      </StatusBanner>
      <div className="space-y-1">
        <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
          {t('diagnosis.nextAction')}
        </p>
        <NextAction action={action} shopLocale={shopLocale} />
      </div>
      {children}
    </section>
  );
}

/** A compact evidence table, or "none" when empty. */
function EvidenceTable({
  title,
  headers,
  rows,
}: {
  readonly title: string;
  readonly headers: readonly string[];
  readonly rows: ReadonlyArray<{
    readonly key: string;
    readonly cells: readonly ReactNode[];
  }>;
}) {
  const { t } = useLocale();
  return (
    <div className="space-y-2">
      <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
      {rows.length === 0 ? (
        <p className="text-sm text-gray-500">{t('diagnosis.evidence.none')}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                {headers.map((header) => (
                  <th key={header}>{header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.key}>
                  {row.cells.map((cell, index) => (
                    <td key={index} className="text-xs">
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** The records the verdicts were read from, for whoever wants to check them. */
function Evidence({ diagnosis }: { readonly diagnosis: Diagnosis }) {
  const { t, locale } = useLocale();
  const { order, evidence } = diagnosis;
  const mono = (value: string | null | undefined) => (
    <span className="font-mono">{value ?? '—'}</span>
  );

  return (
    <details className="card space-y-4">
      <summary className="cursor-pointer text-base font-semibold text-gray-900">
        {t('diagnosis.evidence.title')}
      </summary>
      <p className="text-sm text-gray-600">{t('diagnosis.evidence.hint')}</p>

      <dl className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Fact label={t('diagnosis.order.name')}>{order.orderName ?? '—'}</Fact>
        <Fact label={t('diagnosis.order.id')}>{mono(order.orderId)}</Fact>
        <Fact label={t('diagnosis.order.createdAt')}>
          {when(order.createdAt, locale)}
        </Fact>
        <Fact label={t('diagnosis.order.processedAt')}>
          {when(order.processedAt, locale)}
        </Fact>
        <Fact label={t('diagnosis.order.financialStatus')}>
          {mono(order.financialStatus)}
        </Fact>
        <Fact label={t('diagnosis.order.source')}>
          {mono(order.sourceName)}
        </Fact>
        <Fact label={t('diagnosis.order.cancelledAt')}>
          {when(order.cancelledAt, locale)}
        </Fact>
        <Fact label={t('diagnosis.order.beforeAnchor')}>
          <YesNo value={order.createdBeforeAnchor ?? null} />
        </Fact>
        <Fact label={t('diagnosis.order.pos')}>
          <YesNo value={order.exemptByChannel ?? null} />
        </Fact>
        <Fact label={t('diagnosis.order.shopify')}>
          {order.lookupBasis ? t(LOOKUP_BASIS_KEYS[order.lookupBasis]) : '—'}
          {order.lookupError ? (
            <span className="block text-xs text-gray-500">
              {order.lookupError}
            </span>
          ) : null}
        </Fact>
        <Fact label={t('diagnosis.shop.numbering')}>
          <YesNo value={evidence.shop.numberingConfigured} />
        </Fact>
        <Fact label={t('diagnosis.shop.subscription')}>
          {mono(evidence.shop.subscriptionStatus)}
        </Fact>
        <Fact label={t('diagnosis.shop.avv')}>
          <YesNo value={evidence.shop.avvAccepted} />
        </Fact>
        <Fact label={t('diagnosis.shop.autoSend')}>
          <YesNo value={!evidence.shop.disableAutoSend} />
        </Fact>
        <Fact label={t('diagnosis.shop.anchor')}>
          {when(evidence.shop.invoicingAnchor, locale)}
        </Fact>
      </dl>

      <EvidenceTable
        title={t('diagnosis.evidence.documents')}
        headers={[
          t('diagnosis.column.type'),
          t('diagnosis.column.number'),
          t('diagnosis.column.status'),
          t('diagnosis.column.issueDate'),
          t('diagnosis.column.pdf'),
        ]}
        rows={evidence.documents.map((row) => ({
          key: row.id,
          cells: [
            t(
              documentNameKey(row.type, row.disputeId ? 'DISPUTE' : null) ?? '',
            ),
            mono(row.number),
            <>
              {mono(row.status)}
              {!row.isActive && (
                <Badge tone="gray">{t('diagnosis.column.superseded')}</Badge>
              )}
            </>,
            when(row.issueDate, locale),
            <YesNo key="pdf" value={row.hasPdf} />,
          ],
        }))}
      />
      <EvidenceTable
        title={t('diagnosis.evidence.webhooks')}
        headers={[
          t('diagnosis.column.topic'),
          t('diagnosis.column.status'),
          t('diagnosis.column.received'),
          t('diagnosis.column.error'),
        ]}
        rows={evidence.webhookEvents.map((row) => ({
          key: row.id,
          cells: [
            mono(row.topic),
            mono(row.status),
            when(row.createdAt, locale),
            row.errorMessage ?? '—',
          ],
        }))}
      />
      <EvidenceTable
        title={t('diagnosis.evidence.attentionItems')}
        headers={[
          t('diagnosis.column.type'),
          t('diagnosis.column.status'),
          t('diagnosis.column.since'),
          t('diagnosis.column.resolvedBy'),
        ]}
        rows={evidence.attentionItems.map((row) => ({
          key: row.id,
          cells: [
            mono(row.type),
            mono(row.status),
            when(row.createdAt, locale),
            row.resolvedBy ?? '—',
          ],
        }))}
      />
      <EvidenceTable
        title={t('diagnosis.evidence.failedJobs')}
        headers={[
          t('diagnosis.column.queue'),
          t('diagnosis.column.error'),
          t('diagnosis.column.attempts'),
          t('diagnosis.column.since'),
        ]}
        rows={evidence.failedJobs.map((row) => ({
          key: row.id,
          cells: [
            mono(row.queueName),
            row.failedReason ?? '—',
            row.attemptsMade,
            when(row.createdAt, locale),
          ],
        }))}
      />
      <EvidenceTable
        title={t('diagnosis.evidence.refunds')}
        headers={[t('diagnosis.column.refund'), t('diagnosis.column.amount')]}
        rows={(evidence.refunds ?? []).map((row) => ({
          key: row.refundId,
          cells: [mono(row.refundId), mono(row.gross)],
        }))}
      />
      <EvidenceTable
        title={t('diagnosis.evidence.disputes')}
        headers={[
          t('diagnosis.column.dispute'),
          t('diagnosis.column.status'),
          t('diagnosis.column.type'),
        ]}
        rows={evidence.disputes.map((row) => ({
          key: row.disputeId,
          cells: [mono(row.disputeId), mono(row.status), mono(row.type)],
        }))}
      />
    </details>
  );
}

/**
 * The order diagnosis: "why does order #1234 have no invoice / credit note / Storno?"
 *
 * Leads with one plain-language sentence per family and exactly one next action (AC-1.2), and
 * keeps the records it was read from folded away underneath. Read-only: every action is a link to
 * the tool that does it, with its own reason and confirmation, or a sentence for the merchant —
 * support resolves no attention item (AC-G1a). The order being diagnosed lives in `?order=`, so a
 * link to a diagnosis can be pasted into a ticket.
 */
export function DiagnosisTab() {
  const { t } = useLocale();
  const { shopId, file, requireAccess } = useShopFile();
  const [searchParams, setSearchParams] = useSearchParams();
  const order = (searchParams.get('order') ?? '').trim();
  const [input, setInput] = useState(order);
  const verdictText = useVerdictText();

  const diagnosis = useQuery({
    queryKey: ['diagnosis', shopId, order],
    queryFn: () => diagnoseOrder(shopId, order),
    enabled: order !== '',
    retry: false,
  });

  const accessRefused =
    diagnosis.isError && isShopAccessRequired(diagnosis.error, shopId);
  useEffect(() => {
    if (accessRefused) requireAccess();
  }, [accessRefused, requireAccess]);

  function submit(event: FormEvent) {
    event.preventDefault();
    const next = new URLSearchParams(searchParams);
    if (input.trim()) next.set('order', input.trim());
    else next.delete('order');
    setSearchParams(next);
  }

  const data = diagnosis.data;
  const shopLocale = file.identity.locale;

  return (
    <PageBody>
      <section className="card space-y-3">
        <p className="text-sm text-gray-600">{t('diagnosis.intro')}</p>
        <form onSubmit={submit} className="flex flex-wrap items-end gap-3">
          <div className="min-w-[min(16rem,100%)] flex-1">
            <label htmlFor="diagnosis-order" className="field-label">
              {t('diagnosis.form.label')}
            </label>
            <input
              id="diagnosis-order"
              type="text"
              value={input}
              onChange={(event) => setInput(event.target.value)}
              placeholder="#1234"
              autoComplete="off"
              className="field-input font-mono"
            />
            <p className="field-hint">{t('diagnosis.form.hint')}</p>
          </div>
          <button
            type="submit"
            className="btn-primary"
            disabled={input.trim() === '' || diagnosis.isFetching}
          >
            {t('diagnosis.form.submit')}
          </button>
        </form>
      </section>

      {diagnosis.isFetching && (
        <StatusBanner tone="loading">{t('diagnosis.loading')}</StatusBanner>
      )}
      {diagnosis.isError && !accessRefused && (
        <StatusBanner tone="error">
          {opsErrorMessage(diagnosis.error, t('diagnosis.failed'))}
        </StatusBanner>
      )}

      {data && !diagnosis.isFetching && (
        <>
          <p className="text-sm text-gray-600">
            {t('diagnosis.orderLine', {
              order: data.order.orderName ?? data.order.input,
              id: data.order.orderId ?? '—',
            })}
          </p>

          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
            <FamilyCard
              title={t('diagnosis.family.invoice')}
              verdict={data.invoice.verdict}
              documentType={data.invoice.documentType}
              action={data.invoice.action}
              shopLocale={shopLocale}
            />
            <FamilyCard
              title={t('diagnosis.family.corrections')}
              verdict={data.corrections.verdict}
              documentType={data.corrections.documentType}
              subject={
                data.corrections.items.find(
                  (item) =>
                    item.verdict === data.corrections.verdict &&
                    item.documentId === data.corrections.documentId,
                )?.subject
              }
              action={data.corrections.action}
              shopLocale={shopLocale}
            >
              {data.corrections.items.length > 1 && (
                <div className="space-y-1">
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                    {t('diagnosis.family.allCorrections')}
                  </p>
                  <ul className="space-y-1 text-sm text-gray-800">
                    {data.corrections.items.map((item) => (
                      <li key={`${item.subject}-${item.subjectId}`}>
                        <Badge tone="gray">
                          {t(SUBJECT_KEYS[item.subject])}
                        </Badge>{' '}
                        {verdictText(
                          item.verdict,
                          item.documentType,
                          item.subject,
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </FamilyCard>
          </div>

          <Evidence diagnosis={data} />

          {data.auditLogId && (
            <p className="text-xs text-gray-500">
              {t('diagnosis.audited', { id: data.auditLogId })}
            </p>
          )}
        </>
      )}
    </PageBody>
  );
}
